<script setup lang="ts">
import { PriceOverrideStatus } from "@classes-service-types/price-override-status.ts";
import Button from "primevue/button";
import Menu from "primevue/menu";
import { computed, ref, toRefs } from "vue";
import ApproveDeclineModal from "@/components/OperationsApp/components/Modals/ApproveDeclineModal/index.vue";
import { priceOverrideTablePresets } from "@/components/OperationsApp/components/PriceOverrideTable/styles.ts";
import type { ApprovalStatusProps } from "@/components/OperationsApp/components/PriceOverrideTable/types.ts";
import { REVERTED_OVERRIDE_REASON_ID } from "@/components/OperationsApp/constants.ts";
import { hasPriceOverrideApproverPermission } from "@/composables/authorization";
import { STATUS_TO_LABEL_MAP } from "./constants.ts";

const props = withDefaults(defineProps<ApprovalStatusProps>(), {});
const { data } = toRefs(props);
const approveDeclineModalVisible = ref(false);
const newStatusOnUpdate = ref();

const menu = ref();
const items = ref([
  {
    items: [
      {
        label: "Approve",
        command: () => {
          approveDeclineModalVisible.value = true;
          newStatusOnUpdate.value = PriceOverrideStatus.Approved;
        },
        visible: data.value.status === PriceOverrideStatus.NeedsReview,
      },
      {
        label: "Decline",
        command: () => {
          approveDeclineModalVisible.value = true;
          newStatusOnUpdate.value = PriceOverrideStatus.Rejected;
        },
        visible: data.value.status === PriceOverrideStatus.NeedsReview,
      },
      {
        label: "Revert",
        command: () => {
          approveDeclineModalVisible.value = true;
          newStatusOnUpdate.value = PriceOverrideStatus.Reverted;
        },
        visible: data.value.status === PriceOverrideStatus.Approved,
      },
    ],
  },
]);

const toggle = (event) => {
  menu.value.toggle(event);
};

const labelText = computed(() =>
  STATUS_TO_LABEL_MAP[data.value.status] ? STATUS_TO_LABEL_MAP[data.value.status] : data.value.status,
);
const approvalButtonEnabled = computed(
  () =>
    (data.value.status === PriceOverrideStatus.NeedsReview || data.value.status === PriceOverrideStatus.Approved) &&
    hasPriceOverrideApproverPermission.value &&
    data.value.override_reason_id !== REVERTED_OVERRIDE_REASON_ID,
);
</script>

<template>
  <ApproveDeclineModal :data="data" @hide="approveDeclineModalVisible = false" :modalVisible="approveDeclineModalVisible" :updated-status="newStatusOnUpdate"/>
  <div class="card flex justify-content-center">
    <Button
      class="w-40 !h-10 !border-gray-300 !rounded-full mx-auto"
      :pt="priceOverrideTablePresets.approvalButton"
      :label="labelText"
      type="button"
      icon="pi pi-ellipsis-v"
      outlined
      @click="toggle"
      aria-haspopup="true"
      aria-controls="overlay_menu"
      :disabled="!approvalButtonEnabled"
    >
      <template #icon>
        <span class="material-symbols-outlined text-surface-700">keyboard_arrow_down</span>
      </template>
    </Button>
    <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" :pt="priceOverrideTablePresets.approvalMenu" />
  </div>
</template>
